import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Col,
  Row,
  Statistic,
  Typography,
  Button,
  Space,
  Divider,
  Tooltip,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  DollarOutlined,
  RightOutlined,
  ScheduleOutlined,
  WalletOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import { IFinancial, ISchedule } from '../../shared/interfaces';
import {
  ContainerDashboard,
  DashboardHeader,
  DashboardHero,
  DashboardQuickActions,
  MetricCard,
  SectionTitle,
} from './styles';
import SchedulesDay from './SchedulesDay';
import FinancialToPay from './FinancialToPay';
import FinancialToReceive from './FinancialToReceive';
import { Loading } from '../../components';
import dayjs from 'dayjs';
import { useI18n } from '../../shared/i18n';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { t } = useI18n();
  const [modalSchedulesDay, setModalSchedulesDay] = useState<boolean>(false);
  const [modalFinancialToPay, setModalFinancialToPay] =
    useState<boolean>(false);
  const [modalFinancialToReceive, setModalFinancialToReceive] =
    useState<boolean>(false);
  const [events, setEvents] = useState<ISchedule[]>([]);
  const [financialsToPay, setFinancialstoPay] = useState<IFinancial[]>([]);
  const [financialsToReceive, setFinancialsReceive] = useState<IFinancial[]>(
    [],
  );
  const [whatsappUsage, setWhatsappUsage] = useState<number>(0);
  const [receive, setReceive] = useState<number>(0);
  const [pay, setPay] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const loadFinancials = useCallback(async () => {
    const today = new Date().toLocaleDateString('fr-CA');
    try {
      const response = await api.post('/financials/list', {
        keyword: '',
        status: 1,
        filter_by: 'due_date',
        date_initial: '2020-01-01',
        date_final: today,
        take: null,
      });
      const { data, toPay, toReceive } = response.data;

      setReceive(toReceive);
      setPay(toPay);
      setFinancialstoPay(data.filter((x: IFinancial) => x.type === 0));
      setFinancialsReceive(data.filter((x: IFinancial) => x.type === 1));
    } catch (err) {
      addToast({
        type: 'error',
        title: t('dashboard.error.title'),
        description: t('dashboard.error.financials'),
      });
    }
  }, [addToast, t]);

  const loadSchedules = useCallback(async () => {
    const today = new Date().toLocaleDateString('fr-CA');
    try {
      const response = await api.post('/schedules/list', {
        start: today,
        end: today,
      });
      setEvents(response.data);
    } catch {
      addToast({
        type: 'error',
        title: t('dashboard.error.title'),
        description: t('dashboard.error.schedules'),
      });
    }
  }, [addToast, t]);

  const loadWhatsappUsage = useCallback(async () => {
    try {
      const now = dayjs();
      const response = await api.get('/whatsapp/billing/usage', {
        params: {
          month: now.month() + 1,
          year: now.year(),
        },
      });
      setWhatsappUsage(response.data?.data?.total_sent || 0);
    } catch (err) {
      console.error('Falha ao sincronizar uso de API WhatsApp', err);
    }
  }, []);

  const initDashboard = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSchedules(), loadFinancials(), loadWhatsappUsage()]);
    setLoading(false);
  }, [loadSchedules, loadFinancials, loadWhatsappUsage]);

  useEffect(() => {
    initDashboard();
  }, [initDashboard]);

  const cardStyle = {
    borderRadius: '22px',
    boxShadow: '0 18px 40px rgba(18, 62, 51, 0.08)',
    border: 'none',
  };

  return (
    <ContainerDashboard>
      <DashboardHero>
        <DashboardHeader>
          <div>
            <Text className="eyebrow">{t('dashboard.hero.badge')}</Text>
            <Title level={2} style={{ margin: 0 }}>
              {t('dashboard.hero.greeting', { name: user.name })}
            </Title>
            <Text className="subtitle">{t('dashboard.hero.subtitle')}</Text>
          </div>
        </DashboardHeader>

        <DashboardQuickActions>
          <Button
            type="primary"
            size="large"
            icon={<ScheduleOutlined />}
            onClick={() => navigate('/schedules')}
          >
            {t('dashboard.actions.viewSchedule')}
          </Button>
          <Button
            size="large"
            icon={<WalletOutlined />}
            onClick={() => navigate('/financials')}
          >
            {t('dashboard.actions.goFinancials')}
          </Button>
        </DashboardQuickActions>
      </DashboardHero>

      {loading ? (
        <Loading load />
      ) : (
        <>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <MetricCard
                hoverable
                style={cardStyle}
                actions={[
                  <Button
                    type="link"
                    key="view"
                    onClick={() => setModalSchedulesDay(true)}
                  >
                    {t('dashboard.actions.details')}
                  </Button>,
                  <Tooltip title={t('dashboard.tooltips.goSchedule')} key="go">
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={() => navigate('/schedules')}
                    />
                  </Tooltip>,
                ]}
              >
                <Statistic
                  title={
                    <Text strong type="secondary">
                      {t('dashboard.cards.schedulesToday')}
                    </Text>
                  }
                  value={events.length}
                  prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{
                    color: '#1890ff',
                    fontSize: '32px',
                    fontWeight: 'bold',
                  }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">
                  {t('dashboard.cards.schedulesDescription')}
                </Text>
              </MetricCard>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Badge.Ribbon text={t('dashboard.cards.attention')} color="red">
                <MetricCard
                  hoverable
                  style={cardStyle}
                  actions={[
                    <Button
                      type="link"
                      danger
                      key="view"
                      onClick={() => setModalFinancialToPay(true)}
                    >
                      {t('dashboard.actions.overdueAccounts')}
                    </Button>,
                    <Button
                      type="text"
                      key="go"
                      icon={<RightOutlined />}
                      onClick={() => navigate('/financials')}
                    />,
                  ]}
                >
                  <Statistic
                    title={
                      <Text strong type="secondary">
                        {t('dashboard.cards.overduePayables')}
                      </Text>
                    }
                    value={pay}
                    precision={2}
                    suffix={<ArrowUpOutlined style={{ color: '#cf1322' }} />}
                    prefix="R$"
                    valueStyle={{
                      color: '#cf1322',
                      fontSize: '32px',
                      fontWeight: 'bold',
                    }}
                  />
                  <Divider style={{ margin: '12px 0' }} />
                  <Text type="secondary">
                    {t('dashboard.cards.pendingTitles', {
                      count: financialsToPay.length,
                    })}
                  </Text>
                </MetricCard>
              </Badge.Ribbon>
            </Col>

            {/* CARD CONTAS A RECEBER */}
            <Col xs={24} sm={12} lg={8}>
              <MetricCard
                hoverable
                style={cardStyle}
                actions={[
                  <Button
                    type="link"
                    key="view"
                    onClick={() => setModalFinancialToReceive(true)}
                  >
                    {t('dashboard.actions.receivables')}
                  </Button>,
                  <Button
                    type="text"
                    key="go"
                    icon={<RightOutlined />}
                    onClick={() => navigate('/financials')}
                  />,
                ]}
              >
                <Statistic
                  title={
                    <Text strong type="secondary">
                      {t('dashboard.cards.receivablesToday')}
                    </Text>
                  }
                  value={receive}
                  precision={2}
                  suffix={<ArrowDownOutlined style={{ color: '#3f8600' }} />}
                  prefix="R$"
                  valueStyle={{
                    color: '#3f8600',
                    fontSize: '32px',
                    fontWeight: 'bold',
                  }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">
                  {t('dashboard.cards.expectedInvoices', {
                    count: financialsToReceive.length,
                  })}
                </Text>
              </MetricCard>
            </Col>

            {/* WHATSAPP: CONSUMO API */}
            <Col xs={24} sm={12} lg={8}>
              <MetricCard
                hoverable
                style={cardStyle}
                actions={[
                  <Button
                    type="link"
                    key="view"
                    onClick={() => navigate('/whatsapp/usage')}
                  >
                    {t('dashboard.actions.report')}
                  </Button>,
                  <Tooltip
                    title={t('dashboard.tooltips.whatsappSettings')}
                    key="go"
                  >
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={() => navigate('/connections')}
                    />
                  </Tooltip>,
                ]}
              >
                <Statistic
                  title={
                    <Text strong type="secondary">
                      {t('dashboard.cards.whatsappMonth')}
                    </Text>
                  }
                  value={whatsappUsage}
                  prefix={<WhatsAppOutlined style={{ color: '#25D366' }} />}
                  valueStyle={{
                    color: '#25D366',
                    fontSize: '28px',
                    fontWeight: 'bold',
                  }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">
                  {t('dashboard.cards.whatsappDescription')}
                </Text>
              </MetricCard>
            </Col>
          </Row>

          <SectionTitle>{t('dashboard.actions.quickTitle')}</SectionTitle>

          <Space size="middle" wrap>
            <Button
              type="primary"
              size="large"
              icon={<WalletOutlined />}
              onClick={() => navigate('/financials')}
            >
              {t('dashboard.actions.cashFlow')}
            </Button>
            <Button
              size="large"
              icon={<ScheduleOutlined />}
              onClick={() => navigate('/schedules')}
            >
              {t('dashboard.actions.fullSchedule')}
            </Button>
            <Button
              size="large"
              icon={<DollarOutlined />}
              onClick={() => navigate('/financials')}
            >
              {t('dashboard.actions.createExpense')}
            </Button>
          </Space>
        </>
      )}

      {/* Renderização Condicional dos Modais */}
      {modalSchedulesDay && (
        <SchedulesDay
          open={modalSchedulesDay}
          close={() => setModalSchedulesDay(false)}
          events={events}
        />
      )}
      {modalFinancialToPay && (
        <FinancialToPay
          open={modalFinancialToPay}
          close={() => setModalFinancialToPay(false)}
          financials={financialsToPay}
          toPay={pay}
        />
      )}
      {modalFinancialToReceive && (
        <FinancialToReceive
          toReceive={receive}
          open={modalFinancialToReceive}
          close={() => setModalFinancialToReceive(false)}
          financials={financialsToReceive}
        />
      )}
    </ContainerDashboard>
  );
};

export default Dashboard;
